import { DEPTH_BINS, REEF_ISLANDS, binTopM } from '../../sim/reef';
import type { MauiNuiReef } from './data';
import { noise } from './floor';

/**
 * Where on Maui Nui Reef each of its strata shows (M118, the user's fifth brief: "Construct one continuous reef
 * environment using a weighted blend of all reef ecological inputs", "Do not assign one island to one part of the
 * screen in an obvious way", "The scene can have subtle internal variation from shallower-feeling to slightly
 * deeper-feeling areas, but this must be gradual and seamless"). Every point of the habitat has a weight for each of
 * the twelve strata, summing to 1, by which its coral, its bleaching, its bottom and its fish are drawn there:
 *
 * - **Along the reef** (z, the camera's left to right): the reef's floor ordered by depth, each band given its share of
 *   the habitat's length by its share of the reef's floor (so the shallower-feeling end draws on the shallow band, the
 *   middle on the middle band, the deeper-feeling end on the deep), each band's weight easing into the next's over `softQ`
 *   of the length either side of where they meet, by a step symmetric about it, so each band's weight over the whole
 *   length is its share of the floor exactly.
 * - **Within a band**, its islands each by its floor in the band, swung up and down by a smooth field of its own
 *   (`noise`, `cellM` across, up to `swing` of its weight either way), so the reef's character drifts patch by patch
 *   with no island's anywhere its own and no line between them.
 *
 * The weights are the drawing's, by the reef's fixed seed; the simulation's strata are read through them, never changed.
 */
export interface ReefBlend {
  /** Each band's weight at a place along the reef (`DEPTH_BINS` order), summing to 1. */
  bandsAt(z: number): [number, number, number];
  /** The reef's own depth there by its floor, m, smoothed: from the reef's top at one end to its foot at the other. */
  depthAt(z: number): number;
  /** Each stratum's weight at a point (the reef's `strata` order), summing to 1. */
  strataAt(x: number, z: number): number[];
  /** Each island's weight at a point (`REEF_ISLANDS` order), its strata's summed. */
  islandsAt(x: number, z: number): number[];
}

/** A symmetric step from 0 to 1 over -1 to 1 (smoothstep's), so its integral across is that of a sharp one. */
const step = (t: number): number => {
  const u = Math.min(1, Math.max(0, (t + 1) / 2));
  return u * u * (3 - 2 * u);
};

/**
 * The blend for a reef laid along `lengthM` of the habitat, its middle at z = 0.
 * @param softQ how far either side of where two bands meet the one eases into the other, as a share of the length
 * @param cellM the islands' fields' scale, m
 * @param swing how far an island's field swings its weight either way, a share of it
 */
export function reefBlend(reef: MauiNuiReef, lengthM: number, softQ: number, cellM: number, swing: number): ReefBlend {
  const bins = DEPTH_BINS.length;
  const bandShare = DEPTH_BINS.map((_, k) => reef.strata.reduce((s, l, j) => s + (l.binIndex === k ? (reef.areaShare[j] as number) : 0), 0));
  // Where each band begins and ends along the reef, as shares of its length.
  const edges = [0];
  for (const share of bandShare) edges.push((edges[edges.length - 1] as number) + share);
  // Each band's depths (its top and its foot, m), and the easing kept within the narrowest band's half.
  const tops = DEPTH_BINS.map((_, k) => binTopM(k));
  const feet = DEPTH_BINS.map((b) => b.bottomM);
  const soft = Math.max(1e-6, Math.min(softQ, ...bandShare.filter((x) => x > 0).map((x) => x / 2)));
  // The share of the reef's floor shallower than a place along the reef (0 at the shallower-feeling end, 1 at the deeper).
  const quantileAt = (z: number): number => Math.min(1, Math.max(0, z / lengthM + 0.5));
  const bandsAt = (z: number): [number, number, number] => {
    const q = quantileAt(z);
    // How far past each band's start the point is, 0 to 1 (past the first always, the last's end never); a band's
    // weight how far past its own start less how far past the next's.
    const past = edges.map((e, k) => (k === 0 ? 1 : k === bins ? 0 : step((q - e) / soft)));
    return DEPTH_BINS.map((_, k) => (past[k] as number) - (past[k + 1] as number)) as [number, number, number];
  };
  // Each band's depth at q, its top to its foot across its share, carried on straight past it; blended by the bands' weights.
  const depthAt = (z: number): number => {
    const q = quantileAt(z);
    const w = bandsAt(z);
    return w.reduce((d, wk, k) => {
      const [lo, hi] = [edges[k] as number, edges[k + 1] as number];
      const across = hi > lo ? (q - lo) / (hi - lo) : 0;
      return d + wk * ((tops[k] as number) + across * ((feet[k] as number) - (tops[k] as number)));
    }, 0);
  };
  const strataAt = (x: number, z: number): number[] => {
    const bands = bandsAt(z);
    // Each island's swing here, the same in every band.
    const swung = REEF_ISLANDS.map((_, i) => 1 + swing * (2 * noise(reef.seed, 20 + i, x, z, cellM) - 1));
    const sums = DEPTH_BINS.map((_, k) => reef.strata.reduce((s, l, j) => s + (l.binIndex === k ? (reef.areaShare[j] as number) * (swung[l.islandIndex] as number) : 0), 0));
    return reef.strata.map((l, j) => {
      const sum = sums[l.binIndex] as number;
      return sum > 0 ? ((bands[l.binIndex] as number) * (reef.areaShare[j] as number) * (swung[l.islandIndex] as number)) / sum : 0;
    });
  };
  const islandsAt = (x: number, z: number): number[] => {
    const w = strataAt(x, z);
    return REEF_ISLANDS.map((_, i) => reef.strata.reduce((s, l, j) => s + (l.islandIndex === i ? (w[j] as number) : 0), 0));
  };
  return { bandsAt, depthAt, strataAt, islandsAt };
}
